import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { NavHeader } from "@/components/nav-header";
import { SpaceBackground } from "@/components/space-background";
import { CursorGlow } from "@/components/cursor-glow";
import { ErrorBoundary } from "@/components/error-boundary";
import NotFound from "@/pages/not-found";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import Landing from "@/pages/landing";
import Login from "@/pages/login";
import BrowseClasses from "@/pages/browse-classes";
import ClassDetail from "@/pages/class-detail";
import SessionLobby from "@/pages/session-lobby";
import LiveSession from "@/pages/live-session";
import ProviderDashboard from "@/pages/provider-dashboard";
import AdminDashboard from "@/pages/admin-dashboard";
import CalendarPage from "@/pages/calendar";
import RecordingLibrary from "@/pages/recording-library";
import RecordingDetailPage from "@/pages/recording-detail";
import JoinRoom from "@/pages/join-room";

function PageTransition({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("page-transition-enter");
    void el.offsetWidth;
    el.classList.add("page-transition-enter");
  }, [location]);

  return <div ref={ref} className="page-transition-enter">{children}</div>;
}

function Router() {
  return (
    <PageTransition>
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/calendar" component={CalendarPage} />
        <Route path="/classes" component={BrowseClasses} />
        <Route path="/classes/:classId" component={ClassDetail} />
        <Route path="/sessions/:sessionId" component={SessionLobby} />
        <Route path="/session/:sessionId" component={LiveSession} />
        <Route path="/live/:sessionId" component={LiveSession} />
        <Route path="/recordings" component={RecordingLibrary} />
        <Route path="/recordings/:recordingId" component={RecordingDetailPage} />
        <Route path="/join/:slug" component={JoinRoom} />
        <Route path="/provider" component={ProviderDashboard} />
        <Route path="/admin" component={AdminDashboard} />
        <Route component={NotFound} />
      </Switch>
    </PageTransition>
  );
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language);
    if (lang) {
      document.documentElement.dir = lang.dir;
      document.documentElement.lang = lang.code;
    }
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <TooltipProvider>
              <div className="flex min-h-screen flex-col overflow-x-hidden">
                <SpaceBackground />
                <CursorGlow />
                <NavHeader />
                <main className="relative flex-1">
                  <ErrorBoundary>
                    <Router />
                  </ErrorBoundary>
                </main>
              </div>
              <Toaster />
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
